import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, PopupData, RecordData, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';

import ClientMetricsPopupComponent from 'webapp/components/client/metrics/popup';

import type { ClientMetricRecord } from 'webapp/models';
import ClientMetric from 'webapp/models-custom/client-metric';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

type RequestVariablePrefix = 'clientMetrics' | 'clientMetricsData';

export default class ModelManagerClientMetricsService extends ModelManagerForStoreService<
	ClientMetricRecord,
	ClientMetric
> {
	@service('request') declare requestService: Service.Request;
	@service('socket') declare socketService: Service.Socket;

	@tracked sourceCountPerClientPerType: Record<string, Record<string, number>> = {}; // {<space-id>: {<source-type-id>: <count>, ...}, ...}

	private _clientMetricsModels: ClientMetric[] = [];

	@cached
	get clientMetricsModels(): ClientMetric[] {
		this.updateModelCache(this._clientMetricsModels, this.savedRecords);
		return this._clientMetricsModels;
	}

	openClientMetricsPopup(data: PopupData.ClientMetrics.Edit) {
		const popup = this.popupService.createModal(ClientMetricsPopupComponent, {
			popupClass: 'popup-type-client-metrics',
			allowStacked: true, // metric select
		});

		popup.open(data);
	}

	// ------ Requests ------

	_clientMetricsRequested = false;
	_clientMetricsRequestPromise: Promise<void> | null = null;
	requestClientMetrics(preStoreFn?: () => Promise<void>): Promise<void> {
		return this.requestOnce(
			'clientMetrics',
			() => {
				return this.requestService
					.api<APIData.ClientMetrics.ListResponse>('GET', '/client-metrics')
					.then((payload) => {
						if (payload.clientSourcesCount) {
							this.sourceCountPerClientPerType = Object.assign(
								{},
								payload.clientSourcesCount,
							);
							delete payload.clientSourcesCount;
						}
						return payload;
					});
			},
			preStoreFn,
		);
	}

	_clientMetricsDataRequested = false;
	_clientMetricsDataRequestPromise: Promise<void> | null = null;
	requestClientMetricsData(preStoreFn?: () => Promise<void>): Promise<void> {
		// data itself will be sent async over pusher after data request
		// so we need to trigger it after loading metrics themselves to avoid metric data being garbage-collected
		const preRequestPromise = this.requestClientMetrics().then(
			() => this.socketService.afterDataChannelSubscribed,
		);

		return preRequestPromise.then(() => {
			void this.requestOnce(
				'clientMetricsData',
				() => {
					return this.requestService.api<RecordData.Payload>(
						'GET',
						'/client-metrics/data',
					);
				},
				preStoreFn,
			);
		});
	}

	requestOnce(
		variablePrefix: RequestVariablePrefix,
		requestPromiseFn: () => Promise<object | void>,
		preStoreFn?: () => Promise<void>,
	): Promise<void> {
		return this.requestService.dataRequestOnce<RequestVariablePrefix>(
			this,
			variablePrefix,
			requestPromiseFn,
			preStoreFn,
		);
	}

	// ------ For-store ------

	modelName = ModelName.CLIENT_METRIC;

	createModel(model: ClientMetricRecord): ClientMetric {
		return new ClientMetric({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/client-metrics': ModelManagerClientMetricsService;
	}
}
